import { cloneDeep } from "lodash";
import { ISiteKey } from "../site/reducer";
import toPlainError, { IPlainError } from "../../../error/toPlainError";
import { createRandomId } from "../../../utils";
import {
  IInitialGCPRequest,
  IInitialGcpRequestFlightMarker,
  IInitialGcpRequestFlightMarkerCoordinates,
  IInitialGcpRequestInstalledFlightMarker,
  IInitialGcpRequestSuggestedFlightMarker,
} from "../../../apiFacades/bapi/initialGcpRequest";

export const INITIALGCPREQUEST_FETCH_REQUESTED =
  "INITIALGCPREQUEST_FETCH_REQUESTED";
export const INITIALGCPREQUEST_FETCH_FULFILLED =
  "INITIALGCPREQUEST_FETCH_FULFILLED";
export const putInitialGcpRequestFetchFulfilled = (
  initialGcpRequest: IInitialGCPRequest,
) => ({
  type: INITIALGCPREQUEST_FETCH_FULFILLED,
  payload: initialGcpRequest,
});

export const INITIALGCPREQUEST_FETCH_REJECTED =
  "INITIALGCPREQUEST_FETCH_REJECTED";
export const INITIALGCPREQUEST_UPDATE_REQUESTED =
  "INITIALGCPREQUEST_UPDATE_REQUESTED";
export const INITIALGCPREQUEST_UPDATE_FULFILLED =
  "INITIALGCPREQUEST_UPDATE_FULFILLED";
export const INITIALGCPREQUEST_UPDATE_REJECTED =
  "INITIALGCPREQUEST_UPDATE_REJECTED";
export const INITIALGCPREQUEST_NEW_NAIL_ADDED =
  "INITIALGCPREQUEST_NEW_NAIL_ADDED";
export const INITIALGCPREQUEST_NEW_NAIL_REMOVED =
  "INITIALGCPREQUEST_NEW_NAIL_REMOVED";
export const INITIALGCPREQUEST_NEW_NAIL_UPDATED =
  "INITIALGCPREQUEST_NEW_NAIL_UPDATED";
export const INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED =
  "INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED";
export const INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED =
  "INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED";

export const fetchInitialGCPRequest = (
  siteKey: ISiteKey,
): IAction<ISiteKey> => ({
  type: INITIALGCPREQUEST_FETCH_REQUESTED,
  payload: siteKey,
});

export interface IUpdateInitialGCPRequestParams {
  siteKey: ISiteKey;
  data: IInitialGCPRequest;
}

export const updateInitialGCPRequest = (
  siteKey: ISiteKey,
  data: IInitialGCPRequest,
): IAction<IUpdateInitialGCPRequestParams> => ({
  type: INITIALGCPREQUEST_UPDATE_REQUESTED,
  payload: {
    siteKey,
    data,
  },
});

const createSuggestedFlightMarker = (
  coordinates: IInitialGcpRequestFlightMarkerCoordinates,
): IInitialGcpRequestSuggestedFlightMarker => ({
  id: createRandomId(10),
  tsCreatedMs: Date.now(),
  coordinates,
});

export const addSuggestedFlightMarkerToInitialGcpRequest = (
  coordinates: IInitialGcpRequestFlightMarkerCoordinates,
): IAction<IInitialGcpRequestSuggestedFlightMarker> => ({
  type: INITIALGCPREQUEST_NEW_NAIL_ADDED,
  payload: createSuggestedFlightMarker(coordinates),
});

export const installSuggestedFlightMarkerInInitialGcpRequest = (
  suggestedFlightMarker: IInitialGcpRequestSuggestedFlightMarker,
): IAction<IInitialGcpRequestSuggestedFlightMarker> => ({
  type: INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED,
  payload: suggestedFlightMarker,
});

export const uninstallSuggestedFlightMarkerInInitialGcpRequest = (
  suggestedFlightMarkerId: string,
): IAction<string> => ({
  type: INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED,
  payload: suggestedFlightMarkerId,
});

export const removeNewNail = (id: string): IAction<string> => ({
  type: INITIALGCPREQUEST_NEW_NAIL_REMOVED,
  payload: id,
});

export interface IInitialGCPRequestState {
  data: IInitialGCPRequest;
  pending: boolean;
  error: IPlainError;
}

export const defaultState: IInitialGCPRequestState = {
  data: {
    suggestedFlightMarkers: [],
    installedFlightMarkers: [],
  },
  pending: false,
  error: null,
};
export default function gcpRequestReducer(
  state: IInitialGCPRequestState = defaultState,
  action: IAction<any>,
): IInitialGCPRequestState {
  switch (action.type) {
    case INITIALGCPREQUEST_UPDATE_REQUESTED:
      return {
        ...state,
        pending: true,
      };
    case INITIALGCPREQUEST_FETCH_REQUESTED:
      return {
        ...defaultState,
        pending: true,
      };
    case INITIALGCPREQUEST_UPDATE_FULFILLED:
      return state;
    case INITIALGCPREQUEST_FETCH_FULFILLED:
      return {
        ...defaultState,
        data: action.payload,
      };
    case INITIALGCPREQUEST_UPDATE_REJECTED:
    case INITIALGCPREQUEST_FETCH_REJECTED:
      return {
        ...defaultState,
        error: toPlainError(action.payload),
      };
    case INITIALGCPREQUEST_NEW_NAIL_ADDED:
      return {
        ...defaultState,
        data: mAddSuggestedFlightMarker(state.data, action.payload),
      };
    case INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED:
      return {
        ...defaultState,
        data: mAddInstalledFlightMarker(state.data, action.payload),
      };
    case INITIALGCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED:
      return {
        ...defaultState,
        data: mRemoveInstalledFlightMarker(state.data, action.payload),
      };
    case INITIALGCPREQUEST_NEW_NAIL_UPDATED:
      return {
        ...defaultState,
        data: mUpdateSuggestedFlightMarker(
          state.data,
          action.payload.newNail,
          action.payload.index,
        ),
      };
    case INITIALGCPREQUEST_NEW_NAIL_REMOVED:
      return {
        ...defaultState,
        data: mRemoveSuggestedFlightMarker(state.data, action.payload),
      };
    default:
      return state;
  }
}

// these functions have prefix "m" (modifier) to avoid collisions with corresponding action creators

const mAddSuggestedFlightMarker = (
  data: IInitialGCPRequest,
  suggestedFlightMarker: IInitialGcpRequestSuggestedFlightMarker,
): IInitialGCPRequest => ({
  ...data,
  suggestedFlightMarkers: [
    ...data.suggestedFlightMarkers,
    suggestedFlightMarker,
  ],
});

const mAddInstalledFlightMarker = (
  data: IInitialGCPRequest,
  installedFlightMarker: IInitialGcpRequestInstalledFlightMarker,
): IInitialGCPRequest => {
  return {
    ...data,
    installedFlightMarkers: [
      ...data.installedFlightMarkers.filter(
        (marker) => marker.id !== installedFlightMarker.id,
      ),
      installedFlightMarker,
    ],
  };
};

const mRemoveInstalledFlightMarker = (
  data: IInitialGCPRequest,
  flightMarkerId: string,
) => ({
  ...data,
  installedFlightMarkers: data.installedFlightMarkers.filter(
    ({ id }) => id !== flightMarkerId,
  ),
});
const mUpdateSuggestedFlightMarker = (
  data: IInitialGCPRequest,
  updatedSuggestedFlightMarker: IInitialGcpRequestSuggestedFlightMarker,
  index: number,
) => {
  const suggestedFlightMarkers = cloneDeep(data.suggestedFlightMarkers);
  if (index < 0 || index >= suggestedFlightMarkers.length) {
    throw new Error("invalid index");
  }
  suggestedFlightMarkers[index] = updatedSuggestedFlightMarker;
  return {
    ...data,
    suggestedFlightMarkers,
  };
};

const mRemoveSuggestedFlightMarker = (
  data: IInitialGCPRequest,
  flightMarkerId: string,
) => ({
  ...data,
  suggestedFlightMarkers: data.suggestedFlightMarkers.filter(
    ({ id }) => id !== flightMarkerId,
  ),
});

export const getSuggestedFlightMarkerById = (
  state: IInitialGCPRequestState,
  flightMarkerId: string,
) =>
  state.data.suggestedFlightMarkers.find(({ id }) => id === flightMarkerId) ||
  null;

export const getInstalledFlightMarkerById = (
  state: IInitialGCPRequestState,
  flightMarkerId: string,
) =>
  state.data.installedFlightMarkers.find(({ id }) => id === flightMarkerId) ||
  null;

export const getInitialGCPRequestInstalledMarkersList = (
  state: IInitialGCPRequestState,
): IInitialGcpRequestInstalledFlightMarker[] =>
  state.data.installedFlightMarkers;

/**
 * @deprecated Use getPotentialFlightMarkers instead
 */
export const isInitialGcpRequestSuggestedFlightMarkerInstalled = (
  state: IInitialGCPRequestState,
  suggestedFlightMarkerId: string,
): boolean => {
  return getInitialGCPRequestInstalledMarkersList(state).some(
    (installedMarker: IInitialGcpRequestInstalledFlightMarker) =>
      installedMarker.id === suggestedFlightMarkerId,
  );
};

export const getActualInitialGCPRequestMarker = (
  initialGCPRequestState: IInitialGCPRequestState,
  flightMarkerId: string,
): IInitialGcpRequestFlightMarker => {
  const isInstalled = isInitialGcpRequestSuggestedFlightMarkerInstalled(
    initialGCPRequestState,
    flightMarkerId,
  );
  return isInstalled
    ? getInstalledFlightMarkerById(initialGCPRequestState, flightMarkerId)
    : getSuggestedFlightMarkerById(initialGCPRequestState, flightMarkerId);
};
